import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { CheckCircle2, XCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import { PageSpinner } from '@/components/ui/spinner'
import { Logo } from '@/components/logo'

/**
 * Landing page the user is bounced to after returning from an MCP OAuth
 * provider (e.g. Notion, Linear). The OAuth flow opens a new tab; this
 * route lives in that tab. Their actual conversation with Iris is back
 * in the originating tab — we just confirm the connection landed and
 * point them back.
 *
 * Status + provider come in as query params from `mcp-oauth-callback`:
 *   ?mcp_status=pending|error&mcp_detail=<provider-or-reason>&mcp_confirm=<code>
 *
 * A new connection is applied only when this page confirms it with the code
 * and the signed-in user's session, so only the owner's own browser can
 * activate it. A signed-out tab signs in first and comes back with the code.
 */

const searchSchema = z.object({
  mcp_status: z.enum(['pending', 'error']).optional(),
  mcp_detail: z.string().optional(),
  mcp_confirm: z.string().optional(),
})

function McpCallbackPage() {
  const { mcp_status, mcp_detail, mcp_confirm } = Route.useSearch()
  const canConfirm = mcp_status === 'pending' && !!mcp_detail && !!mcp_confirm

  const { data: confirmed, isPending } = useQuery({
    queryKey: ['mcp-oauth-confirm', mcp_detail, mcp_confirm],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke(
        'mcp-oauth-confirm',
        { body: { provider: mcp_detail, confirm: mcp_confirm } }
      )
      return !error && data?.connected === true
    },
    enabled: canConfirm,
    retry: false,
    staleTime: Infinity,
  })

  if (canConfirm && isPending) {
    return <PageSpinner message='Finishing the connection...' />
  }

  const isSuccess = canConfirm && confirmed === true

  // Browsers refuse `window.close()` for tabs they didn't open with
  // window.open() — the user opened this tab by clicking a link. We try
  // anyway (some configurations allow it) and fall back to the explicit
  // "return to your previous tab" instruction.
  const handleClose = () => window.close()

  return (
    <div className='flex min-h-screen flex-col items-center justify-center px-4'>
      <div className='mx-auto flex w-full max-w-md flex-col items-center text-center'>
        <Logo className='mb-10 w-16' />
        {isSuccess ? (
          <CheckCircle2
            className='mb-6 h-16 w-16 text-emerald-500'
            strokeWidth={1.5}
          />
        ) : (
          <XCircle
            className='text-destructive mb-6 h-16 w-16'
            strokeWidth={1.5}
          />
        )}
        <h1 className='font-heading text-2xl tracking-tight'>
          {isSuccess
            ? mcp_detail
              ? `Connected to ${mcp_detail}`
              : 'Connected'
            : 'Connection failed'}
        </h1>
        <p className='text-muted-foreground mt-3 text-sm leading-relaxed'>
          {isSuccess
            ? 'You can return to your previous tab — Iris is ready to use your new tools.'
            : `Something went wrong${mcp_detail ? ` (${mcp_detail})` : ''}. Return to your previous tab and ask Iris to try again.`}
        </p>
        <Button variant='outline' className='mt-8' onClick={handleClose}>
          Close this window
        </Button>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/auth/mcp-callback')({
  validateSearch: searchSchema,
  beforeLoad: ({ context, search, location }) => {
    if (search.mcp_status === 'pending' && !context.auth.isAuthenticated) {
      throw redirect({ to: '/sign-in', search: { redirect: location.href } })
    }
  },
  component: McpCallbackPage,
})
